import { ColumnBoxFlexFullWidth, FlexBoxFullWidth } from '../../boxes';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Modal } from '../../Modal';
import { PositionModalContext } from './PositionEntityModal';
import { Button } from '@mui/material';
import { TEXTS } from '../../../../core/texts';
import { Person } from '@mui/icons-material';
import ProjectPositionFormItem from '../ProjectPositionFormItem';
import { IPositionsState } from '../../../project-opportunities/create-project-opportunity/CreateProjectOpportunity';
import { ValidationErrorState } from '../../../../types/validationErrorState';
import { IProjectPosition, IProjectPositionStringSkills } from '../../../../interfaces/projectPosition';
import { Validator } from '../../../../core/Validator';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { parseAxiosError } from '../../../../services/axios';
import { ButtonWithLoader } from '../../ButtonWithLoader';
import UpdateSkills from './UpdateSkills';
import NAMES = Validator.NAMES;
import { HeaderTab } from '../../HeaderTab';
import { getSkillsInStringArr } from '../../../../utils/getSkillsInStringArr';
import { useNotification } from '../../../../+state/useNotification';
import { equalObjectValues } from '../../../../utils/equalObjectValues';

const KEY = 'edit';

const UpdatePosition = ({ positionEntity }: { positionEntity: IProjectPosition | IProjectPositionStringSkills }) => {
  const [skillsMode, setSkillsMode] = useState(false);
  const { openSuccessToast, openErrorToast } = useNotification();
  const {
    onClose,
    languageCodes,
    positionsWithSkills,
    handleUpdatePosition,
    refreshPositions,
    handleRemoveSkillsFromPosition,
    handleAddNewSkillsToPosition
  } = useContext(PositionModalContext);
  const toggleMode = useCallback(() => setSkillsMode(prev => !prev), []);
  const [_position, setPosition] = useState<IPositionsState<IProjectPosition | IProjectPositionStringSkills>>({ [KEY]: positionEntity });
  const position = useMemo(() => {
    const { skills, ...rest } = _position[KEY];
    return rest;
  }, [_position]);

  const [skills, setSkills] = useState<string[]>(getSkillsInStringArr(positionEntity.skills));
  const initialPosition = useMemo(() => {
    const { skills, ...rest } = positionEntity;
    return rest;
  }, [positionEntity]);
  const initialSkills = useMemo(() => getSkillsInStringArr(positionEntity.skills), [positionEntity]);
  const skillsList = useMemo(() =>
    positionsWithSkills.find(p => p.id === positionEntity.positionId)?.skills.map(s => s.name) || [], [positionsWithSkills, positionEntity.positionId]);

  const [validationErrors, setValidationErrors] = useState<ValidationErrorState>({});

  const mutationPosition = useMutation({
    mutationFn: () => handleUpdatePosition(position.id, position),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      refreshPositions();
      onClose();
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err)),
  });

  const mutationSkills = useMutation({
    mutationFn: ({ updatedSkills, toRemove }: { updatedSkills: string[], toRemove?: boolean }) => toRemove
      ? handleRemoveSkillsFromPosition(position.id, updatedSkills)
      : handleAddNewSkillsToPosition(position.id, updatedSkills),
    onSuccess: () => {
      openSuccessToast(TEXTS.SUCCESS_SAVED);
      refreshPositions();
      onClose();
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err))
  });

  const handleSubmitPosition = useCallback(() => {
    const mainErrors = Validator.validate(position, NAMES.POSITION);
    const rateErrors = Validator.validate(position.preferredRate, NAMES.RATE);
    const avblErrors = Validator.validate(position.availability, NAMES.AVAILABILITY);
    const languageErrors = Validator.validate(position.language, NAMES.LANGUAGE);
    if ([mainErrors, rateErrors, avblErrors, languageErrors].filter(e => !!e).length) {
      return setValidationErrors({ ...mainErrors, ...rateErrors, ...avblErrors, ...languageErrors });
    }
    mutationPosition.mutate();
  }, [mutationPosition, position]);

  const handleSubmitSkills = useCallback(() => {
    const validErrors = Validator.validate({ skills }, NAMES.POSITION);
    if (validErrors) {
      return setValidationErrors(validErrors);
    }
    const toRemove = initialSkills.filter(s => !skills.includes(s));
    const toAdd = skills.filter(s => !initialSkills.includes(s));
    if (toRemove.length) {
      mutationSkills.mutate({ updatedSkills: toRemove, toRemove: true });
    }
    if (toAdd.length) {
      mutationSkills.mutate({ updatedSkills: toAdd });
    }
  }, [initialSkills, skills, mutationSkills]);

  const isLoading = useMemo(() => mutationPosition.isLoading || mutationSkills.isLoading, [mutationSkills, mutationPosition]);
  const disabledSaveBtn = useMemo(() => {
    if (skillsMode) {
      return skills.length === initialSkills.length && !skills.find(skill => !initialSkills.includes(skill));
    }
    return equalObjectValues(position, initialPosition);
  }, [skills, initialSkills, skillsMode, initialPosition, position]);

  return (
    <Modal onClose={onClose} maxWidth={800} height={'90vh'} title={TEXTS.UPDATE_POSITION}
           headerIcon={<Person color={'primary'}/>}
           dialogActions={<FlexBoxFullWidth justifyContent={'space-between'} p={1}>
             <Button variant={'outlined'} color={'error'} onClick={onClose}>{TEXTS.CANCEL}</Button>
             <ButtonWithLoader loading={isLoading} variant={'outlined'} disabled={disabledSaveBtn}
                               onClick={skillsMode ? handleSubmitSkills : handleSubmitPosition}>
               {skillsMode ? TEXTS.SAVE_SKILLS : TEXTS.SAVE_POSITION}
             </ButtonWithLoader>
           </FlexBoxFullWidth>}
    >
      <ColumnBoxFlexFullWidth>
        <FlexBoxFullWidth>
          <HeaderTab isActive={!skillsMode} text={TEXTS.POSITION} onClick={skillsMode ? toggleMode : () => {}}/>
          <HeaderTab isActive={skillsMode} text={TEXTS.SKILLS} onClick={!skillsMode ? toggleMode : () => {}}/>
        </FlexBoxFullWidth>
        <ColumnBoxFlexFullWidth mt={4}>
          {
            skillsMode
              ? <UpdateSkills skills={skills} setSkills={setSkills} skillsList={skillsList}
                              validationErrors={validationErrors}/>
              : <ProjectPositionFormItem
                position={_position[KEY]}
                positionKey={KEY}
                setPositions={setPosition}
                languageCodes={languageCodes}
                positionsWithSkills={positionsWithSkills}
                validationErrors={validationErrors}
                withHeader={false}
                withSkills={false}
              />
          }
        </ColumnBoxFlexFullWidth>
      </ColumnBoxFlexFullWidth>
    </Modal>

  );
};

export default UpdatePosition;